/**
 * Signed-up patient Firestore listener
 *
 * Subscribes to `signedUpPatients/{signUpId}` only when Firebase Auth uid matches `signUpId`.
 * Clears store patient data when auth is missing or uid mismatches.
 */

import { SignedUpPatient } from '@physiofit-digital/physiofit_types';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect } from 'react';
import { auth } from '../../../firebaseConfig';
import { db } from '../../../firebaseConfig';
import { SIGNED_UP_PATIENT_LOAD_TIMEOUT_MS } from '../services/waitForSignedUpPatientReady';
import { useOnboardingStore } from '../store/onboardingStore';

/**
 * Attaches a Firestore listener when authenticated for the current invitation; updates patient in the store.
 *
 * @return {void}
 */
export const useSignedUpPatientConnect = () => {
  const signUpId = useOnboardingStore((s) => s.signUpId);
  const setSignedUpPatient = useOnboardingStore((s) => s.setSignedUpPatient);
  const setSignedUpPatientStatus = useOnboardingStore((s) => s.setSignedUpPatientStatus);
  const listenerAttempt = useOnboardingStore((s) => s.signedUpPatientListenerAttempt);

  useEffect(() => {
    if (!signUpId?.trim()) {
      return;
    }

    const trimmedSignUpId = signUpId.trim();
    let unsubscribeSnapshot: (() => void) | undefined;
    let loadTimeoutId: number | undefined;

    const clearLoadTimeout = () => {
      window.clearTimeout(loadTimeoutId);
      loadTimeoutId = undefined;
    };

    const clearPatient = () => {
      clearLoadTimeout();
      unsubscribeSnapshot?.();
      unsubscribeSnapshot = undefined;
      setSignedUpPatient(undefined);
      setSignedUpPatientStatus('idle');
    };

    const attachSnapshot = () => {
      const user = auth.currentUser;
      if (!user || user.uid !== trimmedSignUpId) {
        clearPatient();
        return;
      }

      unsubscribeSnapshot?.();
      clearLoadTimeout();
      setSignedUpPatientStatus('loading');
      // The listener stays attached, so data arriving after the timeout still flips the status to ready.
      loadTimeoutId = window.setTimeout(() => setSignedUpPatientStatus('error'), SIGNED_UP_PATIENT_LOAD_TIMEOUT_MS);

      unsubscribeSnapshot = onSnapshot(
        doc(db, 'signedUpPatients', trimmedSignUpId),
        (snap) => {
          // An offline client answers from its empty cache first; only the server can say the invitation is gone.
          if (!snap.exists() && snap.metadata.fromCache) {
            return;
          }
          clearLoadTimeout();
          if (!snap.exists()) {
            setSignedUpPatient(undefined);
            setSignedUpPatientStatus('not_found');
            return;
          }
          setSignedUpPatient(snap.data() as SignedUpPatient);
          setSignedUpPatientStatus('ready');
        },
        () => {
          clearLoadTimeout();
          setSignedUpPatient(undefined);
          setSignedUpPatientStatus('error');
        },
      );
    };

    const unsubscribeAuth = onAuthStateChanged(auth, attachSnapshot);
    attachSnapshot();

    return () => {
      unsubscribeAuth();
      clearPatient();
    };
  }, [signUpId, listenerAttempt, setSignedUpPatient, setSignedUpPatientStatus]);
};
